import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';
import type { AppInboxToastStateItem } from '@src/components/GlobalToasts/AppInboxToast';
import { OMSSliceActions, selectOpenPopoutGroups, selectView } from '@src/components/OMS/OMSSlice';
import { OMSView } from '@src/components/OMS/OMSView';
import type { AppStateListenerStart } from '@src/providers/AppStateProvider/types';
import { logger } from '@talos/kyoko';
import type { AppNotification } from '../AppNotificationsMonitorContainer';
import { getNotificationRenderInfo } from '../getNotificationRenderInfo';
import { appNotificationsApi, appNotificationsStreamingApi } from './AppNotificationsApi';
import {
  archiveAllVisibleNotifications,
  archiveNotification,
  progressNotificationState,
} from './AppNotificationsSliceActions';
import { hasStreamingNotificationDataChanged } from './hasStreamingNotificationDataChanged';
import { BATCH_DISMISS_DEFAULT } from './tokens';

export interface AppNotificationStateItem {
  state: UIAppNotificationState;
  toastInfo: AppInboxToastStateItem;
  notification: AppNotification;
}

export interface AppNotificationsState {
  isReady: boolean;
  /** mapping of latest notifications to their id */
  notificationMap: { [P in string]?: AppNotificationStateItem };
}

const initialState: AppNotificationsState = {
  isReady: false,
  notificationMap: {},
};

/** UI state to the normally progressed order */
export const UIAppNotificationState = {
  new: 1,
  inbox: 2,
  closed: 3,
};
export type UIAppNotificationState = keyof typeof UIAppNotificationState;

const appNotificationsSlice = createSlice({
  name: 'appNotifications',
  initialState,
  reducers: {
    /**
     * Add multiple notifications to the state in a single batch operation.
     * This is more efficient than adding notifications one by one.
     */
    addNotifications: (
      state,
      action: PayloadAction<{
        initialUiState: UIAppNotificationState;
        notifications: Array<{
          notification: AppNotification;
          toastInfo: AppInboxToastStateItem;
        }>;
      }>
    ) => {
      action.payload.notifications.forEach(({ notification, toastInfo }) => {
        const serverState = notification.State;
        const isServerStateClosed = serverState !== 'New';
        const existingUIState = state.notificationMap[notification.NotificationID]?.state;
        const nextUiState = isServerStateClosed ? 'closed' : (existingUIState ?? action.payload.initialUiState);

        // Only update if the notification has actually changed
        const existingNotification = state.notificationMap[notification.NotificationID];
        const hasChanged =
          !existingNotification ||
          existingNotification.notification.State !== notification.State ||
          existingNotification.notification.Timestamp !== notification.Timestamp ||
          existingNotification.state !== nextUiState;

        if (hasChanged) {
          state.notificationMap[notification.NotificationID] = {
            state: nextUiState,
            toastInfo,
            notification,
          };
        }
      });
    },

    /** Move notification to a subsequent state */
    moveAllToInbox: state => {
      Object.values(state.notificationMap).forEach(notification => {
        if (notification?.state === 'new') {
          notification.state = 'inbox';
        }
      });
    },

    // Actions used to trigger middleware effects
    openInbox: _ => {},
    closeInbox: _ => {},
  },
  extraReducers: builder => {
    builder.addCase(
      progressNotificationState,
      (state, action: PayloadAction<{ NotificationID: string; state: UIAppNotificationState; force?: boolean }>) => {
        const existingNotification = state.notificationMap[action.payload.NotificationID];
        const currentState = existingNotification?.state ? UIAppNotificationState[existingNotification.state] : 0;
        const newState = UIAppNotificationState[action.payload.state];
        // Allow forward progression or forced state changes (for error rollback)
        if (existingNotification && (newState > currentState || action.payload.force)) {
          existingNotification.state = action.payload.state;
        }
      }
    );
    // Set the initial state to ready when the notification data is fetched
    builder.addMatcher(
      isAnyOf(
        appNotificationsStreamingApi.endpoints.getStreamingNotifications.matchFulfilled,
        appNotificationsApi.endpoints.getExistingNotifications.matchFulfilled
      ),
      state => {
        state.isReady = true;
      }
    );
  },
});

const anyStreamingOrExisting = isAnyOf(
  appNotificationsStreamingApi.endpoints.getStreamingNotifications.matchFulfilled,
  appNotificationsApi.endpoints.getExistingNotifications.matchFulfilled
);

const isStreamingCacheCleared = isAnyOf(
  appNotificationsStreamingApi.internalActions.removeQueryResult,
  appNotificationsStreamingApi.util.resetApiState
);

const selectNewNotifications = appNotificationsStreamingApi.endpoints.getStreamingNotifications.select({});

export function setupListeners(startListening: AppStateListenerStart) {
  const unsubNotificationUpdate = startListening({
    predicate(action, currentState, originalState) {
      if (anyStreamingOrExisting(action)) {
        return true;
      }
      return hasStreamingNotificationDataChanged(
        selectNewNotifications(currentState).data,
        selectNewNotifications(originalState).data
      );
    },
    effect: (action, api) => {
      // Determine the initial UI state
      // - REST API notifications are always set to 'inbox' (they are existing/old notifications)
      // - Streaming notifications are set to 'new' unless inbox is open
      function getInitialNotificationUIState() {
        const isFromRestApi = appNotificationsApi.endpoints.getExistingNotifications.matchFulfilled(action);

        // REST API notifications are always old/existing notifications, so they go to inbox
        if (isFromRestApi) {
          return 'inbox';
        }

        // For streaming notifications, check if inbox is open
        const currentView = selectView(api.getState());
        const currentPopoutView = selectOpenPopoutGroups(api.getState());
        const initialUiState: 'new' | 'inbox' =
          currentPopoutView.includes('AppInbox') || currentView === OMSView.NotificationsInbox ? 'inbox' : 'new';
        return initialUiState;
      }

      let notifications: AppNotification[];
      const newNotifications = selectNewNotifications(api.getState()).data;
      if (appNotificationsStreamingApi.endpoints.getStreamingNotifications.matchFulfilled(action)) {
        // Handle the action from streamingDataSlice
        notifications = [...action.payload.values()];
      } else if (appNotificationsApi.endpoints.getExistingNotifications.matchFulfilled(action)) {
        // Handle the action from appNotificationsApi
        notifications = action.payload;
      } else if (newNotifications) {
        notifications = [...newNotifications.values()];
      } else if (isStreamingCacheCleared(action)) {
        // RTK Query drops this cache on unsubscribe (after keepUnusedDataFor) or resetApiState.
        // That is expected lifecycle, not a notification update — there is no payload to apply.
        return;
      } else {
        throw new Error(`Unexpected action '${action.type}' type for notification data`);
      }
      const initialUiState = getInitialNotificationUIState();
      // Only process notifications that have actually changed
      const currentState = api.getState().appNotifications.notificationMap;
      const changedNotifications = notifications.filter(notification => {
        const existing = currentState[notification.NotificationID];
        return (
          !existing ||
          existing.notification.State !== notification.State ||
          existing.notification.Timestamp !== notification.Timestamp
        );
      });

      if (changedNotifications.length > 0) {
        // Memoize render info to avoid recalculating for the same notification
        const renderInfoCache = new Map<string, AppInboxToastStateItem>();
        const notificationsWithRenderInfo = changedNotifications.map(notification => {
          let toastInfo = renderInfoCache.get(notification.NotificationID);
          if (!toastInfo) {
            toastInfo = getNotificationRenderInfo(notification);
            renderInfoCache.set(notification.NotificationID, toastInfo);
          }
          return {
            notification,
            toastInfo,
          };
        });

        api.dispatch(
          appNotificationsSliceActions.addNotifications({
            initialUiState,
            notifications: notificationsWithRenderInfo,
          })
        );
      }
    },
  });

  const unsubInboxOpener = startListening({
    actionCreator: appNotificationsSliceActions.openInbox,
    effect: (_, api) => {
      api.dispatch(OMSSliceActions.openView(OMSView.NotificationsInbox));

      // Update the states of all notifications to 'inbox'
      api.dispatch(appNotificationsSliceActions.moveAllToInbox());
    },
  });

  const unsubInboxCloser = startListening({
    actionCreator: appNotificationsSliceActions.closeInbox,
    effect: (_, api) => {
      api.dispatch(OMSSliceActions.closeView());
    },
  });

  const unsubMoveToInboxIfOpen = startListening({
    actionCreator: appNotificationsSliceActions.addNotifications,
    effect: (action, api) => {
      if (api.getState().OMS.openedView === OMSView.NotificationsInbox) {
        api.dispatch(appNotificationsSliceActions.moveAllToInbox());
      }
    },
  });

  const unsubArchiveNotification = startListening({
    actionCreator: archiveNotification,
    effect: async (action, api) => {
      const notificationID = action.payload.NotificationID;

      // Update UI state immediately (optimistic update)
      api.dispatch(
        progressNotificationState({
          NotificationID: notificationID,
          state: 'closed',
        })
      );

      // Send archive request
      const result = await api.dispatch(
        appNotificationsApi.endpoints.archiveNotifications.initiate({
          notificationId: notificationID,
        })
      );

      if (result.error) {
        // Rollback on failure
        logger.error(new Error(`Failed to archive notification ${notificationID}`), {
          extra: result.error,
        });
        api.dispatch(
          progressNotificationState({
            NotificationID: notificationID,
            state: 'inbox',
            force: true,
          })
        );
      }
    },
  });

  const unsubBatchArchive = startListening({
    actionCreator: archiveAllVisibleNotifications,
    effect: async (action, api) => {
      const notificationIDs = action.payload.NotificationIDs;
      // safety fallback in case BatchSize was set to 0
      const batchSize = action.payload.BatchSize || BATCH_DISMISS_DEFAULT;
      const isRetry = action.payload.IsRetry || false;

      // Divide notificationIDs into chunks based on the limit
      const chunks = [];
      for (let i = 0; i < notificationIDs.length; i += batchSize) {
        chunks.push(notificationIDs.slice(i, i + batchSize));
      }

      // Update UI state for all notifications immediately (optimistic update)
      notificationIDs.forEach(notificationID => {
        api.dispatch(
          progressNotificationState({
            NotificationID: notificationID,
            state: 'closed',
          })
        );
      });

      // Process each chunk in sequence
      for (const chunk of chunks) {
        // Send batch archive request for this chunk
        const result = await api.dispatch(
          appNotificationsApi.endpoints.archiveBatchNotifications.initiate({
            NotificationIDs: chunk,
          })
        );

        if (result.error) {
          // Full failure - rollback notifications in this chunk
          logger.error(new Error(`Failed to batch archive notifications chunk`), {
            extra: result.error,
          });
          chunk.forEach(notificationID => {
            api.dispatch(
              progressNotificationState({
                NotificationID: notificationID,
                state: 'inbox',
                force: true,
              })
            );
          });
        } else if (result.data) {
          // Partial failure - rollback only failed notifications in this chunk
          const failedIDs = result.data.data?.[0]?.FailedNotificationIDs || [];
          if (failedIDs.length > 0) {
            if (!isRetry) {
              api.dispatch(
                archiveAllVisibleNotifications({
                  NotificationIDs: failedIDs,
                  BatchSize: batchSize,
                  IsRetry: true,
                })
              );
            } else {
              logger.error(new Error(`Failed to archive ${failedIDs.length} notification(s) in chunk`), {
                extra: { failedIDs },
              });
              failedIDs.forEach(notificationID => {
                api.dispatch(
                  progressNotificationState({
                    NotificationID: notificationID,
                    state: 'inbox',
                    force: true,
                  })
                );
              });
            }
          }
        }
      }
    },
  });

  return () => {
    unsubNotificationUpdate();
    unsubInboxOpener();
    unsubInboxCloser();
    unsubMoveToInboxIfOpen();
    unsubArchiveNotification();
    unsubBatchArchive();
  };
}
export const appNotificationsSliceActions = { ...appNotificationsSlice.actions };
export const appNotificationsSliceReducer = appNotificationsSlice.reducer;
